import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import {
  DraftIncomeSource,
  DocUploadDetail,
  DocUploadPaystubEntry,
  parseDraftDetail,
} from 'src/app/models/income-wizard-workflow';
import { ApplicantDataService } from 'src/app/services/applicant-data.service';

@Component({
  selector: 'app-docupload-wizard-detail',
  templateUrl: './docupload-wizard-detail.component.html',
  styleUrls: ['./docupload-wizard-detail.component.less'],
  animations: [
    trigger('cardEnter', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(12px)' }),
        animate('280ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
})
export class DocuploadWizardDetailComponent implements OnChanges {
  @Input() applicantName = '';
  @Input() existingDraft: DraftIncomeSource | null = null;
  @Input() downloadBusy = false;
  @Output() downloadDocument = new EventEmitter<void>();
  @Output() downloadStub = new EventEmitter<number>();

  overlayImage: SafeUrl | null = null;
  overlayLoading = false;
  activeOverlayIndex = -1;

  stubOverlayImage: SafeUrl | null = null;
  stubOverlayLoading = false;
  activeStubOverlay = -1;
  stubDownloadBusy: Record<number, boolean> = {};

  constructor(private appData: ApplicantDataService, private sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['existingDraft']) {
      this.overlayImage = null;
      this.overlayLoading = false;
      this.activeOverlayIndex = -1;
    }
  }

  get detail(): DocUploadDetail | null {
    return parseDraftDetail<DocUploadDetail>(this.existingDraft);
  }

  get docType(): string {
    return this.detail?.docType ?? 'document';
  }

  get docTypeLabel(): string {
    switch (this.docType) {
      case 'w2': return 'W-2';
      case '1099': return '1099';
      case 'paystub': return 'Paystub';
      default: return 'Document';
    }
  }

  get docTypeIcon(): string {
    switch (this.docType) {
      case 'w2': return '📄';
      case '1099': return '📋';
      case 'paystub': return '💵';
      default: return '📎';
    }
  }

  get employerName(): string {
    return this.detail?.employerName ?? this.existingDraft?.employerName ?? 'Unknown';
  }

  get heroAmount(): number | null {
    if (this.docType === 'w2' || this.docType === '1099') {
      return this.detail?.annualAmount ?? null;
    }
    if (this.docType === 'paystub') {
      return this.detail?.perPeriodGross ?? null;
    }
    return null;
  }

  get heroAmountLabel(): string {
    if (this.docType === 'w2' || this.docType === '1099') return '/year';
    if (this.docType === 'paystub') return '/period';
    return '';
  }

  get suggestedMonthly(): number | null {
    const amt = this.existingDraft?.suggestedAmount;
    return amt != null && amt > 0 ? amt : null;
  }

  get taxYear(): string | null {
    return this.detail?.taxYear ?? null;
  }

  get payFrequency(): string | null {
    return this.detail?.payFrequency ?? null;
  }

  get streamPersonName(): string {
    return this.detail?.recipientName ?? '';
  }

  get nameMatches(): boolean {
    if (!this.applicantName || !this.streamPersonName) return false;
    return this.applicantName.toLowerCase().trim() === this.streamPersonName.toLowerCase().trim();
  }

  get hasDocument(): boolean {
    return !!this.detail?.documentId;
  }

  get fileName(): string | null {
    return this.detail?.fileName ?? null;
  }

  get hasFraudConcern(): boolean {
    return this.authenticityStatus === 'failed'
      || this.ocrClassification === 'unauthorized'
      || (this.fraudSignals.length > 0);
  }

  get isInvalidDocument(): boolean {
    return this.detail?.isInvalidDocument === true;
  }

  get invalidReason(): string | null {
    return this.detail?.invalidReason ?? null;
  }

  get invalidDocCount(): number {
    return this.paystubs.filter(s => s.isInvalidDocument).length;
  }

  get authenticityStatus(): string | null {
    return this.detail?.authenticityStatus ?? null;
  }

  get ocrClassification(): string | null {
    return this.detail?.ocrClassification ?? null;
  }

  get hasOcrError(): boolean {
    const status = this.detail?.ocrStatus;
    return status === 'ERROR' || status === 'PARTIAL_ERROR';
  }

  get ocrErrorMessage(): string {
    if (this.detail?.ocrStatus === 'ERROR')
      return 'All documents failed to load from vendor (data unavailable at time of processing)';
    return 'Some documents failed to load from vendor';
  }

  get authenticityScore(): number {
    return this.detail?.authenticityScore ?? 0;
  }

  get isAuthenticityFailing(): boolean {
    const score = this.authenticityScore;
    return score > 0 && score < 60;
  }

  get fraudSignals(): { code: string; confidence: string; description: string }[] {
    return this.detail?.fraudSignals ?? [];
  }

  get overlayUrls(): string[] {
    return this.detail?.overlayUrls ?? [];
  }

  // ── Paystub-specific ──

  get paystubs(): DocUploadPaystubEntry[] {
    return this.detail?.paystubs ?? [];
  }

  get hasPaystubs(): boolean {
    return this.docType === 'paystub' && this.paystubs.length > 0;
  }

  get paystubsAreSequential(): boolean {
    return !this.detail?.sopChecks?.some(c => c.checkName === 'PayPeriodSequence' && !c.passed);
  }

  paystubNameMatches(stub: DocUploadPaystubEntry): boolean {
    if (!this.applicantName || !stub.employeeName) return false;
    return this.applicantName.toLowerCase().trim() === stub.employeeName.toLowerCase().trim();
  }

  onDownloadClick(): void {
    this.downloadDocument.emit();
  }

  loadOverlay(index: number): void {
    const urls = this.overlayUrls;
    if (index < 0 || index >= urls.length || this.overlayLoading) return;

    if (this.activeOverlayIndex === index) {
      // Toggle off
      this.overlayImage = null;
      this.activeOverlayIndex = -1;
      return;
    }

    this.overlayLoading = true;
    this.overlayImage = null;
    this.activeOverlayIndex = index;

    this.appData.downloadOverlayProxy(urls[index]).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        this.overlayImage = this.sanitizer.bypassSecurityTrustUrl(url);
        this.overlayLoading = false;
      },
      error: () => {
        this.overlayLoading = false;
        this.activeOverlayIndex = -1;
      },
    });
  }

  loadStubOverlay(stubIndex: number, overlayIndex: number): void {
    const stub = this.paystubs[stubIndex];
    const urls = stub?.overlayUrls;
    if (!urls?.length || this.stubOverlayLoading) return;

    if (this.activeStubOverlay === stubIndex) {
      this.stubOverlayImage = null;
      this.activeStubOverlay = -1;
      return;
    }

    this.stubOverlayLoading = true;
    this.stubOverlayImage = null;
    this.activeStubOverlay = stubIndex;

    this.appData.downloadOverlayProxy(urls[overlayIndex]).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        this.stubOverlayImage = this.sanitizer.bypassSecurityTrustUrl(url);
        this.stubOverlayLoading = false;
      },
      error: () => {
        this.stubOverlayLoading = false;
        this.activeStubOverlay = -1;
      },
    });
  }

  onDownloadStub(index: number): void {
    this.downloadStub.emit(index);
  }
}
